import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TwoFactorSetupModalComponent } from '../two-factor-setup-modal/two-factor-setup-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { MFAService } from '@app/pages/login-page/services/verify-totp.service';
import { TWO_FACTOR_DIALOG_OPTIONS } from '@app/utils/constants/two-factor.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { InputType } from '@app/utils/enums/input-type.enum';
import { TwoFactorStatus, PasswordAttempts } from '@app/utils/enums/two-factor.enum';
import { TwoFactorData } from '@app/utils/interfaces/mfa.interface';

@Component({
  selector: 'app-password-confirmation-modal',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule, ReactiveFormsModule, IconComponent],
  templateUrl: './password-confirmation-modal.component.html',
  styleUrl: './password-confirmation-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordConfirmationModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<PasswordConfirmationModalComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly twoFactorService = inject(MFAService);

  protected passwordForm!: FormGroup;
  protected isPasswordVisible = signal(false);
  protected errorMessage = signal('');
  protected failedAttempts = signal(0);
  protected isLoading = signal(false);

  public readonly toggleStatus = inject<{ action: TwoFactorStatus }>(MAT_DIALOG_DATA);
  public inputType = InputType;
  public iconName = IconName;

  public ngOnInit(): void {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required]],
    });
  }

  public onCancel(): void {
    this.dialogRef.close({ completed: false });
  }

  public onConfirm(): void {
    this.errorMessage.set('');

    if (this.validateForm()) {
      this.verifyPassword();
    }
  }

  public togglePasswordVisibility(): void {
    this.isPasswordVisible.set(!this.isPasswordVisible());
  }

  private handleFailedAttempt(): void {
    const attempts = this.failedAttempts() + 1;
    let errorText = '';
    this.failedAttempts.set(attempts);

    if (attempts >= PasswordAttempts.MAX_ATTEMPTS) {
      this.errorMessage.set('');
    } else if (attempts === PasswordAttempts.WARNING_THRESHOLD) {
      errorText = this.translateService.instant(
        'user-profile.security.twoFactor.steps.confirmPassword.twoAttemptsWarning',
      );
      this.errorMessage.set(errorText);
    } else {
      errorText = this.translateService.instant(
        'user-profile.security.twoFactor.steps.confirmPassword.incorrectPassword',
      );
      this.errorMessage.set(errorText);
    }
  }

  private showErrorModal(): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'user-profile.security.twoFactor.errors.errorTitle',
        description: 'user-profile.security.twoFactor.errors.errorText',
      },
    });
  }

  private validateForm(): boolean {
    if (this.passwordForm.valid) {
      return true;
    }

    if (this.passwordForm.get('password')?.hasError('required')) {
      const errorText = this.translateService.instant(
        'user-profile.security.twoFactor.steps.confirmPassword.required',
      );
      this.errorMessage.set(errorText);
    }

    this.passwordForm.markAllAsTouched();

    return false;
  }

  private verifyPassword(): void {
    this.isLoading.set(true);
    const password = this.passwordForm.value.password;

    if (this.toggleStatus.action === TwoFactorStatus.ENABLED) {
      this.enableTwoFactor(password);
    } else {
      this.disableTwoFactor(password);
    }
  }

  private enableTwoFactor(password: string): void {
    this.twoFactorService
      .enable2FA(password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          this.handleEnableSuccess(response.data);
        },
        error: (error) => {
          this.isLoading.set(false);

          if (error.status === 400 || error.status === 401) {
            this.handleFailedAttempt();
          } else {
            this.showErrorModal();
          }
        },
      });
  }

  private disableTwoFactor(password: string): void {
    this.twoFactorService
      .disable2FA(password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.handleDisableSuccess();
        },
        error: (error) => {
          this.isLoading.set(false);

          if (error.status === 400 || error.status === 401) {
            this.handleFailedAttempt();
          } else {
            this.showErrorModal();
          }
        },
      });
  }

  private handleEnableSuccess(data: TwoFactorData): void {
    this.failedAttempts.set(0);

    this.dialogRef.addPanelClass('hidden-dialog');

    const setupDialogRef = this.dialog.open(TwoFactorSetupModalComponent, {
      ...TWO_FACTOR_DIALOG_OPTIONS,
      data,
    });

    setupDialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.completed) {
          this.openSuccessModal(
            'user-profile.security.twoFactor.steps.completion.title',
            'user-profile.security.twoFactor.steps.completion.subtitle',
          );
        } else {
          this.dialogRef.close({ completed: false });
        }
      });
  }

  private openSuccessModal(titleKey: string, descriptionKey: string): void {
    const successDialogRef = this.dialog.open(StatusModalComponent, {
      ...TWO_FACTOR_DIALOG_OPTIONS,
      data: {
        isSuccess: true,
        title: titleKey,
        description: descriptionKey,
      },
    });

    successDialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((successResult) => {
        this.dialogRef.close({
          completed: successResult === true,
        });
      });
  }

  private handleDisableSuccess(): void {
    this.failedAttempts.set(0);

    this.dialogRef.close({
      completed: true,
    });
  }
}
